import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Video, Check } from 'lucide-react';
import { dimensions } from '../data/dimensions';
import ProgressBar from './ProgressBar';
import VideoPlayer from './VideoPlayer';
import VideoRecorder from './VideoRecorder';
import PostureAssessment from './PostureAssessment';

const OnboardingFlow = ({ progress, onUpdateProgress, onComplete }) => {
  const [showRecorder, setShowRecorder] = useState(false);
  const [currentPosture, setCurrentPosture] = useState(null);

  // Calculate total postures across all dimensions
  const totalPostures = dimensions.reduce((total, dim) => total + dim.postures.length, 0);
  const completedCount = progress.completedPostures.length;

  // Get current dimension and posture
  const currentDimension = dimensions.find(dim => dim.id === progress.currentDimension);
  const currentPostureIndex = progress.currentPosture;
  const posture = currentDimension?.postures[currentPostureIndex];

  useEffect(() => {
    setCurrentPosture(posture);
  }, [posture]);

  const handleNext = () => {
    if (!currentDimension || !posture) return;

    const isLastPostureInDimension = currentPostureIndex >= currentDimension.postures.length - 1;
    const isLastDimension = progress.currentDimension >= dimensions.length;

    if (isLastPostureInDimension) {
      if (isLastDimension) {
        // Assessment complete
        onComplete();
      } else {
        // Move to next dimension
        onUpdateProgress({
          currentDimension: progress.currentDimension + 1,
          currentPosture: 0
        });
      }
    } else {
      // Move to next posture in current dimension
      onUpdateProgress({
        currentPosture: currentPostureIndex + 1
      });
    }
  };

  const handlePrevious = () => {
    if (currentPostureIndex > 0) {
      // Previous posture in current dimension
      onUpdateProgress({
        currentPosture: currentPostureIndex - 1
      });
    } else if (progress.currentDimension > 1) {
      // Previous dimension, last posture
      const prevDimension = dimensions.find(dim => dim.id === progress.currentDimension - 1);
      onUpdateProgress({
        currentDimension: progress.currentDimension - 1,
        currentPosture: prevDimension.postures.length - 1
      });
    }
  };

  const handleStartRecording = () => {
    setShowRecorder(true);
  };

  const handleRecordingComplete = (recordingBlob) => {
    if (!posture) return;

    // Save recording to progress
    const recordings = { ...progress.recordings };
    recordings[posture.id] = {
      blob: recordingBlob,
      timestamp: Date.now(),
      postureId: posture.id,
      dimensionId: currentDimension.id
    };

    // Mark posture as completed
    const completedPostures = [...progress.completedPostures];
    if (!completedPostures.includes(posture.id)) {
      completedPostures.push(posture.id);
    }

    onUpdateProgress({
      recordings,
      completedPostures
    });

    setShowRecorder(false);
  };

  const handleRecordingCancel = () => {
    setShowRecorder(false);
  };

  const isPostureCompleted = posture && progress.completedPostures.includes(posture.id);
  const canProceed = isPostureCompleted;
  const isFirstPosture = progress.currentDimension === 1 && currentPostureIndex === 0;
  const isLastPosture = progress.currentDimension === dimensions.length && 
                        currentPostureIndex >= (currentDimension?.postures.length - 1);

  if (!currentDimension || !posture) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-dark-400">Loading assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950">
      {/* Minimal Header */}
      <div className="border-b border-dark-800">
        <div className="max-w-lg mx-auto px-6 py-6 text-center">
          <div className="text-xs text-dark-500 mb-2">
            {completedCount + 1} of {totalPostures}
          </div>
          <div className="w-full bg-dark-800 rounded-full h-1 mb-4">
            <motion.div
              className="h-full bg-white rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / totalPostures) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <h1 className="text-2xl font-light text-dark-50 mb-1">
            {posture.name}
          </h1>
          <p className="text-sm text-dark-400">
            {posture.description} • {posture.duration}
          </p>
        </div>
      </div>

      {/* Vertically Stacked Content */}
      <div className="max-w-lg mx-auto px-6 py-8 pb-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${progress.currentDimension}-${currentPostureIndex}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <PostureAssessment
              posture={posture}
              isCompleted={isPostureCompleted}
              onStartRecording={handleStartRecording}
              recording={progress.recordings[posture.id]}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Minimal Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-dark-950 border-t border-dark-800 p-4">
        <div className="max-w-lg mx-auto flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={isFirstPosture}
            className={`text-sm ${
              isFirstPosture ? 'text-dark-500' : 'text-dark-300 hover:text-white'
            }`}
          >
            ← Previous
          </button>

          {isPostureCompleted && (
            <motion.div
              className="flex items-center space-x-1 text-green-400"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Check className="w-4 h-4" />
              <span className="text-xs">Complete</span>
            </motion.div>
          )}

          <button
            onClick={handleNext}
            disabled={!canProceed}
            className={`text-sm ${
              canProceed ? 'text-white hover:text-accent-400' : 'text-dark-500'
            }`}
          >
            {isLastPosture ? 'Finish' : 'Next'} →
          </button>
        </div>
      </div>

      {/* Video Recorder Modal */}
      <AnimatePresence>
        {showRecorder && (
          <VideoRecorder
            onRecordingComplete={handleRecordingComplete}
            onCancel={handleRecordingCancel}
            postureTitle={posture.name}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingFlow;
